/**
 * FENLoader - FEN Input and Loading UI
 *
 * Phase 5 - SOC Refactoring (UI Cleanup)
 *
 * Extracted from BoardContainer.tsx to centralize FEN loading functionality.
 * Provides textarea input and validation feedback for loading game positions from FEN strings.
 *
 * RESPONSIBILITIES:
 * - Render FEN input textarea
 * - Handle FEN input changes
 * - Trigger FEN loading
 * - Display load status (success/error messages)
 */

import React, { useState } from 'react';
import styles from './FENLoader.module.css';

interface FENLoaderProps {
  darkMode: boolean;
  onLoadFEN: (fenString: string) => { success: boolean; error?: string };
}

export function FENLoader({ darkMode, onLoadFEN }: FENLoaderProps) {
  const [fenInput, setFenInput] = useState<string>('');
  const [fenLoadStatus, setFenLoadStatus] = useState<{
    success: boolean;
    message: string;
  } | null>(null);

  const handleFenInputChange = (event: React.ChangeEvent<HTMLTextAreaElement>) => {
    setFenInput(event.target.value);
    // Clear status when user starts typing again
    if (fenLoadStatus) {
      setFenLoadStatus(null);
    }
  };

  const handleFenLoad = () => {
    const trimmedFen = fenInput.trim();

    if (!trimmedFen) {
      setFenLoadStatus({
        success: false,
        message: 'Please enter a FEN string',
      });
      return;
    }

    const result = onLoadFEN(trimmedFen);

    if (result.success) {
      setFenLoadStatus({
        success: true,
        message: 'Board loaded successfully!',
      });
      // Optionally clear input after successful load
      setFenInput('');
    } else {
      setFenLoadStatus({
        success: false,
        message: result.error || 'Failed to load FEN',
      });
    }
  };

  return (
    <div
      className={styles.fenLoader}
      style={{
        marginTop: '15px',
        padding: '10px',
        border: `1px solid ${darkMode ? '#555' : '#ccc'}`,
        borderRadius: '4px',
        backgroundColor: darkMode ? '#2a2a2a' : '#f9f9f9',
      }}
    >
      <label
        style={{
          display: 'block',
          marginBottom: '5px',
          fontWeight: 'bold',
          color: darkMode ? '#ffffff' : '#000000',
        }}
      >
        Load from FEN:
      </label>
      <textarea
        value={fenInput}
        onChange={handleFenInputChange}
        placeholder="Paste FEN string here (e.g., copy from current game)..."
        style={{
          width: '100%',
          height: '60px',
          padding: '5px',
          border: `1px solid ${darkMode ? '#555' : '#ccc'}`,
          borderRadius: '4px',
          fontSize: '12px',
          fontFamily: 'monospace',
          backgroundColor: darkMode ? '#1a1a1a' : '#ffffff',
          color: darkMode ? '#ffffff' : '#000000',
          resize: 'vertical',
          boxSizing: 'border-box',
        }}
      />
      <button
        onClick={handleFenLoad}
        style={{
          marginTop: '5px',
          padding: '8px 12px',
          backgroundColor: '#4CAF50',
          color: 'white',
          border: 'none',
          borderRadius: '4px',
          cursor: 'pointer',
          fontSize: '14px',
        }}
        onMouseOver={(e) => (e.currentTarget.style.backgroundColor = '#45a049')}
        onMouseOut={(e) => (e.currentTarget.style.backgroundColor = '#4CAF50')}
      >
        Load Board
      </button>
      {fenLoadStatus && (
        <div
          style={{
            marginTop: '8px',
            padding: '6px',
            borderRadius: '4px',
            fontSize: '12px',
            backgroundColor: fenLoadStatus.success ? '#d4edda' : '#f8d7da',
            color: fenLoadStatus.success ? '#155724' : '#721c24',
            border: `1px solid ${fenLoadStatus.success ? '#c3e6cb' : '#f5c6cb'}`,
          }}
        >
          {fenLoadStatus.message}
        </div>
      )}
    </div>
  );
}
